import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";

import type { ChallengeVerifyMethod } from "../../../api/api-connector";

import { ErrorCode, MFAMethod } from "../../../api";
import {
  ApiConnector,
  apiErrorCode,
  RequestError,
} from "../../../api/api-connector";
import { maybeReportError } from "../../../app/errors";
import { Button } from "../../../components/ui-kit/button";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { useCodeErrorText } from "./code-error";
import { BackupCodeInput, OtpCodeInput } from "./code-input";

type Step =
  | { kind: "code" }
  /** The challenge is gone; only a fresh login can produce another. */
  | { kind: "expired" }
  /** Too many wrong codes — the backend discarded the challenge. */
  | { kind: "exhausted" };

/**
 * The second factor for an `mfa_required` response — from a login, or from the
 * fresh login that re-authenticating asks for. The only ways out are a correct
 * code or giving up; what a verified challenge grants is up to `onVerified`.
 */
export function MfaVerification({
  api,
  challengeId,
  methods,
  onVerified,
  onCancel,
  cancelLabel,
}: {
  api: ApiConnector;
  challengeId: string;
  methods: MFAMethod[];
  onVerified: () => void;
  onCancel: () => void;
  cancelLabel: ReactNode;
}) {
  const { t } = useLingui();
  const verifyErrorText = useCodeErrorText();
  const totpEnrolled = methods.includes(MFAMethod.totp);
  const [step, setStep] = useState<Step>({ kind: "code" });
  // Backup codes are never listed in `methods`, but `/challenge/verify` takes
  // one whenever the account still holds any — so a passkey-only account still
  // has a way in here.
  const [method, setMethod] = useState<ChallengeVerifyMethod>(
    totpEnrolled ? MFAMethod.totp : MFAMethod.backup_code,
  );
  const [error, setError] = useState<unknown>();
  // Bumped to remount the input, which is how a rejected code gets cleared.
  const [attempt, setAttempt] = useState(0);

  async function verify(code: string) {
    setError(undefined);

    try {
      await api.verifyChallenge(challengeId, method, code);
      onVerified();
    } catch (error) {
      maybeReportError(error);
      if (endedTheChallenge(error)) {
        return;
      }

      if (apiErrorCode(error) === ErrorCode.MFA_INVALID_CODE) {
        // A wrong code leaves the challenge usable, so this is a plain retry.
        // The code is cleared — TOTP expires, backup codes are single-use.
        setAttempt((previous) => previous + 1);
      }

      setError(error);
    }
  }

  /**
   * Move a challenge-level failure onto the step that says so.
   *
   * @returns whether the challenge is gone, leaving nothing to retry.
   */
  function endedTheChallenge(error: unknown) {
    if (apiErrorCode(error) === ErrorCode.RATE_LIMIT_EXCEEDED) {
      setStep({ kind: "exhausted" });
      return true;
    }

    // Any 401 other than a rejected code means the challenge itself is gone —
    // expired, or already consumed by a concurrent attempt.
    if (
      error instanceof RequestError &&
      error.response.status === 401 &&
      apiErrorCode(error) !== ErrorCode.MFA_INVALID_CODE
    ) {
      setStep({ kind: "expired" });
      return true;
    }

    return false;
  }

  function switchMethod(next: ChallengeVerifyMethod) {
    setMethod(next);
    setError(undefined);
  }

  if (step.kind !== "code") {
    return (
      <div
        className="flex flex-col items-center"
        data-testid="mfa-verification"
      >
        <ErrorMessage
          message={
            step.kind === "exhausted" ? (
              // The lockout is on the account, not just the challenge, and
              // outlives a fresh login — so don't promise logging back in works.
              <Trans>
                Too many incorrect codes. Please wait a few minutes and log in
                again.
              </Trans>
            ) : (
              <Trans>Your login expired before it was verified.</Trans>
            )
          }
        />
        <Button className="mt-4" onClick={onCancel}>
          {cancelLabel}
        </Button>
      </div>
    );
  }

  const backupCode = method === MFAMethod.backup_code;

  // Every method other than the current one. Only the authenticator depends on
  // what is enrolled, for the reason noted above.
  const offerable: { method: ChallengeVerifyMethod; label: ReactNode }[] = [
    {
      method: MFAMethod.totp,
      label: <Trans>Use your authenticator app</Trans>,
    },
    { method: MFAMethod.backup_code, label: <Trans>Use a backup code</Trans> },
  ];

  const alternatives = offerable.filter(
    (alternative) =>
      alternative.method !== method &&
      (alternative.method !== MFAMethod.totp || totpEnrolled),
  );

  return (
    <div className="flex flex-col items-center" data-testid="mfa-verification">
      <h2 className="text-xl font-semibold mb-4">
        <Trans>Two-factor authentication</Trans>
      </h2>

      {/* Capped on its own — the backup-code row is wider than this. */}
      <p className="text-sm text-center max-w-80">{instructionsFor(method)}</p>

      {backupCode ? (
        <BackupCodeInput
          key={attempt}
          submitLabel={t`Verify`}
          onSubmit={verify}
        />
      ) : (
        <OtpCodeInput key={attempt} submitLabel={t`Verify`} onSubmit={verify} />
      )}

      <ErrorMessage message={error ? verifyErrorText(error) : ""} />

      <div className="flex flex-col items-center gap-2 mt-4">
        {alternatives.map((alternative) => (
          <a
            key={alternative.method}
            onClick={() => {
              switchMethod(alternative.method);
            }}
            className="text-xs text-brand-primary hover:underline cursor-pointer"
          >
            {alternative.label}
          </a>
        ))}

        <a
          onClick={onCancel}
          className="text-xs text-brand-primary hover:underline cursor-pointer"
        >
          {cancelLabel}
        </a>
      </div>
    </div>
  );
}

function instructionsFor(method: ChallengeVerifyMethod) {
  switch (method) {
    case MFAMethod.totp:
      return <Trans>Enter the code from your authenticator app.</Trans>;

    case MFAMethod.backup_code:
      return (
        <Trans>
          Enter one of the backup codes you saved when you set up two-factor
          authentication. Each code works once.
        </Trans>
      );
  }
}
